import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types for document images
interface DocumentImages {
  type?: 'license' | 'passport';
  front?: string;
  back?: string;
  passport?: string;
}

// Interface for identity verification state
interface VerificationState {
  // Verification step tracking
  currentStep: number;
  documentType: 'license-front' | 'passport' | null;
  documentImages: DocumentImages;
  verificationCompleted: boolean;
  applicantId: string | null;

  // Actions to update the state
  setCurrentStep: (step: number) => void;
  setDocumentType: (type: 'license-front' | 'passport' | null) => void;
  setDocumentImages: (images: DocumentImages) => void;
  setVerificationCompleted: (completed: boolean) => void;
  setApplicantId: (id: string | null) => void;
  resetVerification: () => void;
  logout: () => void;
}

// Initial state values
const initialState = {
  currentStep: 1,
  documentType: null,
  documentImages: {},
  verificationCompleted: false,
  applicantId: null,
};

// Create and export the Zustand store
export const useVerificationStore = create<VerificationState>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Methods to update state
      setCurrentStep: (step) => {
        // Check if the step is already set to avoid unnecessary updates
        if (get().currentStep !== step) {
          set({ currentStep: step });
        }
      },

      setDocumentType: (type) => {
        // Check if the type is already set to avoid unnecessary updates
        if (get().documentType !== type) {
          set({ documentType: type });
        }
      },

      setDocumentImages: (images) => {
        // Since images is an object, we can't do a simple equality check
        // Instead, we'll do a shallow update
        set({ documentImages: images });
      },

      setVerificationCompleted: (completed) => {
        // Check to avoid unnecessary updates
        const state = get();
        if (state.verificationCompleted !== completed) {
          set({
            verificationCompleted: completed,
            // Only update currentStep if completed changes and is now true
            ...(completed && state.currentStep !== 4 ? { currentStep: 4 } : {}),
          });
        }
      },

      setApplicantId: (id) => {
        // Check if the ID is already set to avoid unnecessary updates
        if (get().applicantId !== id) {
          set({ applicantId: id });
        }
      },

      // Reset to initial state but preserve the new applicant ID
      resetVerification: () => {
        const currentApplicantId = get().applicantId;
        set({
          ...initialState,
          applicantId: currentApplicantId, // Keep the current applicant ID
        });
      },

      // Logout method - completely reset verification state
      logout: () => {
        set(initialState);
      },
    }),
    {
      name: 'identity-verification-storage', // unique name for localStorage

      // Version it to allow future migrations
      version: 1,

      // Customize how the state is persisted
      // We can optionally filter sensitive data here
      partialize: (state) => ({
        currentStep: state.currentStep,
        documentType: state.documentType,
        documentImages: state.documentImages,
        verificationCompleted: state.verificationCompleted,
        applicantId: state.applicantId,
      }),
    },
  ),
);
